import React, { useEffect, useState, useRef } from 'react';
import PopupBase from "../base/PopupBase";
import CustomButton from "../../components/buttons/CustomButton";
import { TextField, Select, MenuItem, FormControl, FormControlLabel, Checkbox, Box, Typography, Paper, IconButton, Tooltip } from "@mui/material";
import IconSave from '@mui/icons-material/SaveOutlined';
import IconDownload from '@mui/icons-material/DownloadOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import { CreatePDF, CreatePDFBlob } from '../../utils/Utils.js';
import styles from "./styles.module.css";
import { DataProviderInstance } from "../../api/DataProvider";
import DocumentDefaultTemplate from "../../components/documents-templates/DocumentDefaultTemplate";
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import { GetFriendlyCPF } from '../../utils/Utils';
import Alert from '@mui/material/Alert';
import Loading from '../../components/loading/Loading';
import Toast from '../../components/support/Toast';
import { useNavigate } from "react-router-dom";
import { useWhatsAppSender } from '../../contexts/WhatsAppSenderContext';
import { isWhatsAppPolicyError, getWhatsAppErrorMessage } from '../../utils/whatsappErrors';
import { cidCodesText, diagnosesOfAppointment } from '../../pages/appointment/modules/diagnoses/diagnoses';
import { formatMessagePrice } from '../../utils/appointmentWhatsAppUtils';

const CID_TAG = '{appointment-cid}';

function CreateDocumentPopup(props) {

    // `Diagnoses`: diagnósticos do paciente; só os deste atendimento vão para o documento
    const { Data, Diagnoses, OnHide } = props;
    const { CompanyConfig, GetPatientById, SetPatientDocument, GetInvoice, SendWhatsappDocument, GetWhatsappConfig } = DataProviderInstance();
    const navigate = useNavigate();
    const { capabilities } = useWhatsAppSender();

    const initialIndex = -1;
    let templates = [{ id: -1, tag: "Em branco", title: "", text: "" }, ...CompanyConfig.DocumentTemplates];

    const appointmentData = Data;
    const patientData = GetPatientById(appointmentData.patient);
    const professionalData = CompanyConfig.Resources.find((item) => item.id == appointmentData.resource);
    const procedureData = CompanyConfig.Procedures.find((item) => item.Id == appointmentData.procedure);

    const [selectedOption, setSelectedOption] = useState(initialIndex);
    const [currentTitle, setCurrentTitle] = useState(null);
    const [currentText, setCurrentText] = useState(null);

    const pdfContentRef = useRef(null);
    const [invoiceData, setInvoiceData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sign, setSign] = useState(true);
    const [needsPageReload, setNeedsPageReload] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1.0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [includeCid, setIncludeCid] = useState(false);

    const cidText = cidCodesText(diagnosesOfAppointment(Diagnoses, appointmentData.id));

    useEffect(() => {

        setLoading(true);

        if (appointmentData.invoiceId != "0") {

            GetInvoice(appointmentData.invoiceId, (invoice) => {

                // Sem pagamento o endpoint responde 200 com invoice nulo
                setInvoiceData(invoice?.invoice ? invoice : null);
                setLoading(false);
            });
        }
        else {
            setSelectedOption(initialIndex);
            setCurrentTitle(GetCurrentTitle(initialIndex));
            setCurrentText(GetCurrentText(initialIndex));
            setLoading(false);
        }

        return () => {
            setInvoiceData(null);
            setNeedsPageReload(false);
        }

    }, []);

    useEffect(() => {
        setSelectedOption(initialIndex);
    }, [appointmentData, invoiceData]);

    useEffect(() => {
        setCurrentTitle(GetCurrentTitle(selectedOption));
        setCurrentText(GetCurrentText(selectedOption));
    }, [selectedOption]);

    function OnCloseClickHandler() {
        Hide();
    }

    function Hide() {
        OnHide(needsPageReload);
    }

    const [isPrinting, setIsPrinting] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const [isWhatsappConfigured, setIsWhatsappConfigured] = useState(false);

    function showToast(message, severity = 'success') {
        setToast({ open: true, message, severity });
    }

    useEffect(() => {
        GetWhatsappConfig((result) => {
            if (result && result.config && result.config.instance_id && result.config.has_instance_token) {
                setIsWhatsappConfigured(true);
            } else {
                setIsWhatsappConfigured(false);
            }
        });
    }, []);

    // Botão de envio por WhatsApp só aparece quando há credenciais salvas E
    // a política do número (plataforma × próprio) permite documentos
    const canSendWhatsApp = isWhatsappConfigured && capabilities.document;

    function getFileName() {
        let documentTypeName = currentTitle || 'documento';
        let currentDate = new Date();
        let formattedDate = currentDate.getFullYear() + "_" + (currentDate.getMonth() + 1) + "_" + currentDate.getDate() + "_" + currentDate.getHours() + "_" + currentDate.getMinutes();
        let patientName = patientData.name;
        return `${patientName}_${formattedDate}_${documentTypeName}.pdf`.replace(/\s/g, '_');
    }

    /**
     * Texto que vai para o papel. O CID só entra com a caixa marcada (no
     * atestado, depende de autorização do paciente): no lugar de
     * {appointment-cid} ou, se o modelo não tem o campo, numa linha no fim.
     * Aplicado na hora de desenhar, para não desfazer o que foi editado à mão.
     */
    function withCid(text) {
        if (text == null) return text;
        const value = includeCid ? cidText : '';
        if (text.includes(CID_TAG)) return text.replaceAll(CID_TAG, value);
        return value ? `${text}<br><br>${value}` : text;
    }

    const documentText = withCid(currentText);

    const OnSaveHandler = () => {
        SetPatientDocument(appointmentData.patient, dateIssue.format('YYYY-MM-DD'), appointmentData.id, currentTitle, documentText, (response) => {
            setIsSaved(true);
            setNeedsPageReload(true);
        });
    }

    async function OnSendWhatsAppHandler() {
        // Compose full phone with country code (DDI) for Z-API
        const { normalizePhone } = await import('../../utils/phoneUtils');
        const phone = normalizePhone(patientData.phone || patientData.cellphone || '', patientData.countryCode);

        if (!phone) {
            showToast('Paciente não possui telefone cadastrado.', 'error');
            return;
        }

        setIsSendingWhatsApp(true);

        try {
            const content = pdfContentRef.current;

            // Temporarily remove scaling for PDF generation
            const originalTransform = content.style.transform;
            content.style.transform = 'none';

            const pdfBlob = await CreatePDFBlob(content);

            // Restore scaling
            content.style.transform = originalTransform;

            const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(pdfBlob);
            });

            const fileName = getFileName();
            const caption = `Olá, segue o documento: ${currentTitle || 'Documento'}`;

            SendWhatsappDocument(phone, base64, fileName, caption, patientData?.countryCode || '', (result) => {
                if (result && result.success) {
                    showToast('Documento enviado com sucesso pelo WhatsApp!', 'success');
                } else if (result && result.success === false && isWhatsAppPolicyError(result)) {
                    showToast(getWhatsAppErrorMessage(result), 'error');
                } else {
                    showToast('Não foi possível enviar o documento pelo WhatsApp. Tente novamente.', 'error');
                }
                setIsSendingWhatsApp(false);
            });
        } catch (error) {
            showToast('Erro ao gerar o PDF. Tente novamente.', 'error');
            setIsSendingWhatsApp(false);
        }
    }

    function GetFooter() {
        const whatsAppTooltip = !isWhatsappConfigured
            ? 'Documentos saem pelo seu WhatsApp. Conecte seu número em Integrações > WhatsApp para enviar.'
            : !(patientData.phone || patientData.cellphone)
            ? 'Paciente não possui telefone cadastrado.'
            : '';

        return (
            <div className={styles.footer}>
                <div className={styles.actionButtons}>
                    {canSendWhatsApp && (
                        <Tooltip title={whatsAppTooltip} arrow>
                            <span>
                                <CustomButton
                                    variant="outlined"
                                    style="secondary"
                                    label={isSendingWhatsApp ? "Enviando..." : "Enviar por WhatsApp"}
                                    icon={<WhatsAppIcon />}
                                    onClick={OnSendWhatsAppHandler}
                                    enabled={isWhatsappConfigured && !isSendingWhatsApp && !!(patientData.phone || patientData.cellphone)}
                                />
                            </span>
                        </Tooltip>
                    )}
                    <CustomButton
                        variant="outlined"
                        style="primary"
                        label={"Download PDF"}
                        icon={<IconDownload />}
                        onClick={OnDownloadHandler}
                        enabled={!isPrinting}
                    />
                    <CustomButton
                        variant="contained"
                        style="primary"
                        label={"Registrar"}
                        icon={<IconSave />}
                        onClick={OnSaveHandler}
                        enabled={!isSaved}
                    />
                </div>
            </div>
        )
    }

    function OnDownloadHandler() {
        // If in fullscreen mode, exit before downloading
        if (isFullscreen) {
            setIsFullscreen(false);
            setTimeout(() => {
                executeDownload();
            }, 100);
        } else {
            executeDownload();
        }
    }

    function executeDownload() {
        setIsPrinting(true);
        const fileName = getFileName();
        const content = pdfContentRef.current;

        // Temporarily remove scaling for PDF generation
        const originalTransform = content.style.transform;
        content.style.transform = 'none';

        CreatePDF(content, fileName);

        // Restore scaling after PDF generation
        setTimeout(() => {
            content.style.transform = originalTransform;
            setIsPrinting(false);
        }, 100);
    }

    function GetCurrentTitle(index) {
        const template = templates.find((item) => item.id == index);

        return template.title;
    }

    function parseDuration(duration) {
        const [hours, minutes, seconds] = duration.split(':');
        return (parseInt(hours) * 60) + parseInt(minutes);
    }

    function GetCurrentText(index) {
        const template = templates.find((item) => item.id == index);

        const appointmentDate = new Date(appointmentData.date);

        const friendyDate = appointmentDate.toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric' });
        const appointmentStartTime = appointmentDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        // Duração total do agendamento (soma dos procedimentos); cadastro do procedimento só como reserva
        const appointmentEndTime = new Date(appointmentDate.getTime() + (parseDuration(appointmentData.duration || procedureData?.Duration || '00:00:00') * 60000)).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

        const cpf = GetFriendlyCPF(patientData.cpf);
        const price = invoiceData ? invoiceData.invoice.price : appointmentData.price;

        const text = template.text
            .replace("{patient-name}", "" + patientData.name + "")
            .replace("{patient-cpf}", "" + cpf + "")
            .replace("{appointment-date}", "" + friendyDate + "")
            .replace("{appointment-time}", "" + appointmentStartTime + "")
            .replace("{appointment-reason}", "")
            .replace("{appointment-price}", formatMessagePrice(price))
            .replace("{appointment-startTime}", "" + appointmentStartTime + "")
            .replace("{appointment-endTime}", "" + appointmentEndTime + "")
            .replaceAll('\n', '<br>');

        return text;
    }

    function getCurrentDate() {
        return dateIssue.format('DD/MM/YYYY');
    }

    function handleOptionChange(event) {
        setSelectedOption(event.target.value);
        setCurrentTitle(GetCurrentTitle(event.target.value));
        setCurrentText(GetCurrentText(event.target.value));
    }

    const [dateIssue, setDateIssue] = useState(dayjs(appointmentData.date, 'YYYY-MM-DD'));

    function GetContent() {
        // Check if there are document templates (other than the blank one)
        const hasTemplates = CompanyConfig.DocumentTemplates && CompanyConfig.DocumentTemplates.length > 0;

        if (loading) {
            return <Loading />
        }
        else if (currentText == null && currentTitle == null) {
            return <p>Selecione um paciente e um profissional para gerar o documento.</p>
        }
        else {
            return (
                <div className={styles.mainContainer}>
                    {(invoiceData == null) && <Alert severity="error" sx={{ marginBottom: '16px' }}>Consulta não faturada.</Alert>}
                    
                    <div className={styles.contentColumns}>
                        {/* Left Column - Form Inputs */}
                        <Paper className={styles.formColumn}>
                            <div className={styles.formSection}>
                                <div className={styles.formRow}>
                                    <div className={styles.formField}>
                                        <Typography variant="subtitle2">Data de emissão</Typography>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                defaultValue={dateIssue}
                                                format='DD/MM/YYYY'
                                                onChange={(newValue) => setDateIssue(newValue)}
                                                slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                    
                                    <div className={styles.formField}>
                                        <Typography variant="subtitle2">Modelo</Typography>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={selectedOption}
                                                onChange={handleOptionChange}
                                            >
                                                {templates.map((option, index) => (
                                                    <MenuItem key={index} value={option.id}>{option.tag}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                    
                                    <div className={styles.formFieldButton}>
                                        <Typography variant="subtitle2">&nbsp;</Typography>
                                        <CustomButton
                                            style="secondary"
                                            variant="outlined"
                                            icon={<AddOutlinedIcon fontSize="small" />}
                                            onClick={() => {
                                                window.open('/company/templates', '_blank');
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className={styles.formSection}>
                                    <Typography variant="subtitle2">Título do documento</Typography>
                                    <TextField
                                        value={currentTitle}
                                        fullWidth
                                        placeholder="Digite o título do documento"
                                        onChange={(event) => setCurrentTitle(event.target.value)}
                                        size="small"
                                    />
                            </div>
                            
                            <div className={styles.formSection}>
                                <Typography variant="subtitle2">Conteúdo do documento</Typography>
                                <TextField
                                    value={currentText}
                                    multiline
                                    rows={12}
                                    fullWidth
                                    placeholder="Digite o conteúdo do documento"
                                    onChange={(event) => setCurrentText(event.target.value)}
                                />
                            </div>
                            
                            <div className={styles.formSection}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={sign}
                                            onChange={(event) => setSign(event.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Assinar automaticamente"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={includeCid}
                                            disabled={!cidText}
                                            onChange={(event) => setIncludeCid(event.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={cidText ? `Incluir CID (${cidText})` : 'Incluir CID'}
                                />
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {cidText ? 'Só com autorização do paciente.' : 'Nenhum diagnóstico registrado neste atendimento.'}
                                </Typography>
                            </div>
                        </Paper>
                        
                        {/* Right Column - Document Preview */}
                        <div className={styles.previewColumn}>
                            <Typography variant="subtitle1" fontWeight="600" sx={{ marginBottom: 1 }}>
                                Visualização do documento
                            </Typography>
                            <div className={styles.previewContainer}>
                                <div className={styles.zoomControls}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setZoomLevel(prev => Math.max(prev - 0.1, 0.1))}
                                        disabled={zoomLevel <= 0.1}
                                        title="Diminuir zoom"
                                    >
                                        <ZoomOutIcon fontSize="small" />
                                    </IconButton>
                                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', mx: 0.5 }}>
                                        {Math.round(zoomLevel * 100)}%
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => setZoomLevel(prev => Math.min(prev + 0.1, 1.5))}
                                        disabled={zoomLevel >= 1.5}
                                        title="Aumentar zoom"
                                    >
                                        <ZoomInIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => setIsFullscreen(true)}
                                        title="Visualizar em tela cheia"
                                    >
                                        <FullscreenIcon fontSize="small" />
                                    </IconButton>
                                </div>
                                <div className={styles.previewWrapper} style={{ transform: `scale(${zoomLevel})` }}>
                                    <div ref={pdfContentRef}>
                                        <DocumentDefaultTemplate
                                            date={getCurrentDate()}
                                            companyData={CompanyConfig}
                                            patientData={patientData}
                                            professionalData={professionalData}
                                            title={currentTitle}
                                            description={documentText}
                                            autoSign={sign}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            {isFullscreen && (
                                <div className={styles.fullscreenPreview}>
                                    <IconButton
                                        className={styles.closeFullscreen}
                                        onClick={() => setIsFullscreen(false)}
                                        title="Fechar visualização em tela cheia"
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                    <div className={styles.previewWrapper} style={{ transform: 'scale(0.85)' }}>
                                        <div>
                                            <DocumentDefaultTemplate
                                                date={getCurrentDate()}
                                                companyData={CompanyConfig}
                                                patientData={patientData}
                                                professionalData={professionalData}
                                                title={currentTitle}
                                                description={documentText}
                                                autoSign={sign}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )
        }
    }

    return (
        <>
            <PopupBase Title={"Criar Documento"} Content={GetContent()} Footer={GetFooter()} OnClose={OnCloseClickHandler} />
            <Toast
                open={toast.open}
                onClose={() => setToast({ ...toast, open: false })}
                message={toast.message}
                severity={toast.severity}
                autoHideDuration={5000}
            />
        </>
    );
}

export default CreateDocumentPopup;